/**
 * OrderTimeline
 * ─────────────────────────────────────────────────────────────────────────────
 * Vertical timeline of an order's lifecycle — who moved it to each stage, when,
 * from where, and how long it sat in that stage.
 *
 * Props:
 *   orderId         — Mongo ObjectId string (required)
 *   compact         — bool, tighter row height, no meta row (for order list peek)
 *   onError         — optional callback(errorMessage) for parent toast
 *
 * Usage:
 *   <OrderTimeline orderId={order._id} />
 *
 * Philosophy:
 *   - Read-only. Backend is the single source of truth.
 *   - Gracefully handles empty history (shows the "Order placed" anchor only).
 *   - Actor type badges use a stable colour palette so customers instantly see
 *     whether a rider, vendor, or platform admin moved the order.
 */

import React, { useEffect, useState } from "react";
import { Badge, Spinner, Alert } from "react-bootstrap";
import moment from "moment";
import { apiGetOrderTimeline } from "../api/index";

const ACTOR_COLOURS = {
  customer: { bg: "#e3f2fd", fg: "#0d47a1", label: "Customer" },
  vendor: { bg: "#e8f5e9", fg: "#1b5e20", label: "Vendor" },
  rider: { bg: "#fff3e0", fg: "#e65100", label: "Rider" },
  partner: { bg: "#f3e5f5", fg: "#4a148c", label: "Partner" },
  admin: { bg: "#fce4ec", fg: "#880e4f", label: "Lusuku" },
  system: { bg: "#eceff1", fg: "#37474f", label: "System" },
};

const STATUS_META = {
  PROCESSING: { icon: "fa-receipt", colour: "#f39c12" },
  ACCEPTED: { icon: "fa-check-circle", colour: "#27ae60" },
  ASSIGNED: { icon: "fa-user-check", colour: "#2980b9" },
  REJECTED: { icon: "fa-times-circle", colour: "#c0392b" },
  PICKED_UP: { icon: "fa-box-open", colour: "#8e44ad" },
  IN_TRANSIT: { icon: "fa-motorcycle", colour: "#d35400" },
  ARRIVED: { icon: "fa-map-marker-alt", colour: "#16a085" },
  DELIVERED: { icon: "fa-flag-checkered", colour: "#2d7a5f" },
  CANCELLED: { icon: "fa-ban", colour: "#7f8c8d" },
  CUSTOMER_INQUIRY: { icon: "fa-comment", colour: "#3498db" },
};

function formatDuration(ms) {
  if (ms == null) return null;
  if (ms < 1000) return "< 1s";
  const sec = Math.round(ms / 1000);
  if (sec < 60) return `${sec}s`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min} min`;
  const hrs = Math.floor(min / 60);
  const rem = min % 60;
  return rem ? `${hrs}h ${rem}m` : `${hrs}h`;
}

function ActorBadge({ type }) {
  const meta = ACTOR_COLOURS[type] || ACTOR_COLOURS.system;
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 8px",
        fontSize: 11,
        fontWeight: 600,
        borderRadius: 10,
        background: meta.bg,
        color: meta.fg,
      }}
    >
      {meta.label}
    </span>
  );
}

function TimelineEntry({ entry, isLast, compact }) {
  const meta = STATUS_META[entry.status] || { icon: "fa-circle", colour: "#999" };
  const actor = entry.changedBy || {};
  const duration = formatDuration(entry.durationMs);

  return (
    <div style={{ display: "flex", gap: 12, position: "relative", paddingBottom: isLast ? 0 : 18 }}>
      {/* Left rail */}
      <div style={{ position: "relative", width: 28, flexShrink: 0 }}>
        <div
          style={{
            width: 28,
            height: 28,
            borderRadius: "50%",
            background: meta.colour,
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 12,
            zIndex: 1,
            position: "relative",
          }}
        >
          <i className={`fa ${meta.icon}`} />
        </div>
        {!isLast && (
          <div
            style={{
              position: "absolute",
              left: 13,
              top: 28,
              bottom: -4,
              width: 2,
              background: "#e0e0e0",
            }}
          />
        )}
      </div>

      {/* Right body */}
      <div style={{ flex: 1, paddingTop: 2 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap" }}>
          <div>
            <strong style={{ fontSize: 14, textTransform: "capitalize" }}>
              {entry.status.replace(/_/g, " ").toLowerCase()}
            </strong>
            {actor.userType && (
              <span style={{ marginLeft: 8 }}>
                <ActorBadge type={actor.userType} />
              </span>
            )}
            {actor.userName && (
              <span style={{ marginLeft: 6, fontSize: 12, color: "#555" }}>
                by {actor.userName}
              </span>
            )}
          </div>
          <div style={{ fontSize: 11, color: "#888", whiteSpace: "nowrap" }}>
            {moment(entry.timestamp).format("MMM D, h:mm:ss A")}
          </div>
        </div>

        {!compact && (
          <div style={{ marginTop: 3, fontSize: 12, color: "#666" }}>
            {duration && (
              <span>
                <i className="fa fa-hourglass-half" style={{ marginRight: 4, color: "#aaa" }} />
                lasted <strong>{duration}</strong>
              </span>
            )}
            {entry.location?.address && (
              <span style={{ marginLeft: duration ? 14 : 0 }}>
                <i className="fa fa-map-pin" style={{ marginRight: 4, color: "#aaa" }} />
                {entry.location.address}
              </span>
            )}
            {entry.metadata?.reason && (
              <div style={{ marginTop: 4, color: "#b8860b", fontStyle: "italic" }}>
                Reason: {entry.metadata.reason}
              </div>
            )}
            {entry.notes && (
              <div style={{ marginTop: 4, color: "#555" }}>
                {entry.notes}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function OrderTimeline({ orderId, compact = false, onError }) {
  const [entries, setEntries] = useState([]);
  const [currentStatus, setCurrentStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!orderId) return;
    let alive = true;
    setLoading(true);
    setError(null);
    apiGetOrderTimeline(orderId)
      .then((res) => {
        if (!alive) return;
        const data = res?.data?.data || {};
        setEntries(data.entries || []);
        setCurrentStatus(data.currentStatus || null);
      })
      .catch((e) => {
        if (!alive) return;
        const msg = e?.response?.data?.message || e.message || "Failed to load timeline";
        setError(msg);
        if (onError) onError(msg);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [orderId, onError]);

  if (loading) {
    return (
      <div className="text-center py-3">
        <Spinner animation="border" size="sm" /> Loading timeline…
      </div>
    );
  }
  if (error) {
    return <Alert variant="warning" className="my-2">{error}</Alert>;
  }
  if (entries.length === 0) {
    return (
      <Alert variant="light" className="my-2 text-center">
        <i className="fa fa-history" style={{ marginRight: 6 }} />
        No status changes recorded yet.
      </Alert>
    );
  }

  return (
    <div style={{ padding: compact ? "6px 0" : "12px 4px" }}>
      {!compact && currentStatus && (
        <div style={{ marginBottom: 10, fontSize: 13, color: "#555" }}>
          Current status: <Badge bg="success">{currentStatus}</Badge>
        </div>
      )}
      {entries.map((entry, idx) => (
        <TimelineEntry
          key={entry._id || `${entry.status}-${entry.timestamp}`}
          entry={entry}
          isLast={idx === entries.length - 1}
          compact={compact}
        />
      ))}
    </div>
  );
}
